/**
 * Member Profile Edit Page
 * 
 * Edit profile, next of kin, and change password
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import apiClient, { getUploadUrl } from '@/utils/api';
import { toast } from 'sonner';
import MemberLayout from '@/components/MemberLayout';
import MemberAvatar from '@/components/MemberAvatar';
import { 
  User, 
  Users, 
  Lock,
  Save,
  ArrowLeft,
  Eye,
  EyeOff,
  Upload,
  Camera,
  Trash2
} from 'lucide-react';

const MemberProfileEdit = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'profile';
  
  const [profile, setProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Photo upload state
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [profileForm, setProfileForm] = useState({
    username: '',
    title: '',
    contact_number: '',
    work_station: '',
    specialty: '',
    residential_address: ''
  });

  const [kinForm, setKinForm] = useState({
    first_name: '',
    surname: '',
    relationship: '',
    contact_number: '',
    residential_address: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, notifsRes] = await Promise.all([
        apiClient.get('/profile/me'),
        apiClient.get('/notifications/my')
      ]);
      
      const profileData = profileRes.data;
      setProfile(profileData);
      setProfileForm({
        username: profileData.username || '',
        title: profileData.title || '',
        contact_number: profileData.contact_number || '',
        work_station: profileData.work_station || '',
        specialty: profileData.specialty || '',
        residential_address: profileData.residential_address || ''
      });
      setKinForm({
        first_name: profileData.next_of_kin?.first_name || '',
        surname: profileData.next_of_kin?.surname || '',
        relationship: profileData.next_of_kin?.relationship || '',
        contact_number: profileData.next_of_kin?.contact_number || '',
        residential_address: profileData.next_of_kin?.residential_address || ''
      });
      setNotifications(notifsRes.data || []);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    return getUploadUrl(url);
  };

  const handlePhotoUpload = async () => {
    if (!photoFile) {
      toast.error('Please select a photo');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', photoFile);

      await apiClient.post('/profile/me/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Profile photo updated!');
      setPhotoFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async () => {
    try {
      await apiClient.delete('/profile/me/photo');
      toast.success('Profile photo removed');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove photo');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/profile/me', profileForm);
      toast.success('Profile updated successfully!');
      navigate('/dashboard/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleKinUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/profile/me/next-of-kin', kinForm);
      toast.success('Next of kin updated successfully!');
      navigate('/dashboard/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update next of kin');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    try {
      await apiClient.post('/profile/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const unreadNotifications = notifications.filter(n => !n.is_read);

  if (loading) {
    return (
      <MemberLayout currentPage="profile" title="Edit Profile" notificationCount={0}>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout currentPage="profile" title="Edit Profile" notificationCount={unreadNotifications.length}>
      {/* Back Button */}
      <Button 
        variant="ghost" 
        className="mb-4"
        onClick={() => navigate('/dashboard/profile')}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Profile
      </Button>

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User className="w-4 h-4 mr-2 hidden sm:block" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="kin" data-testid="tab-kin">
            <Users className="w-4 h-4 mr-2 hidden sm:block" />
            Next of Kin
          </TabsTrigger>
          <TabsTrigger value="password" data-testid="tab-password">
            <Lock className="w-4 h-4 mr-2 hidden sm:block" />
            Password
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Edit Personal Information</CardTitle>
              <CardDescription>Update your profile details and photo</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Profile Photo Section */}
              <div className="mb-6 p-4 border rounded-lg bg-muted/30">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Profile Photo
                </Label>
                <div className="flex items-start gap-4 mt-3">
                  <div className="flex-shrink-0">
                    {profile?.portrait_photo_url ? (
                      <img 
                        src={getPhotoUrl(profile.portrait_photo_url)} 
                        alt="Profile" 
                        className="w-24 h-24 rounded-full object-cover border-2 border-primary"
                      />
                    ) : (
                      <MemberAvatar firstName={profile?.first_name} lastName={profile?.last_name} className="w-24 h-24 text-2xl" data-testid="profile-edit-initials-avatar" />
                    )}
                  </div>
                  <div className="flex-1 space-y-3">
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                      data-testid="photo-file-input"
                    />
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handlePhotoUpload}
                        disabled={!photoFile || uploadingPhoto}
                        data-testid="upload-photo-btn"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                      {profile?.portrait_photo_url && (
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={handlePhotoDelete}
                          data-testid="remove-photo-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Accepted: PNG, JPEG, WebP. Max size: 5MB.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      value={profileForm.username}
                      onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                      data-testid="username-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <select
                      id="title"
                      className="w-full border rounded-md p-2"
                      value={profileForm.title}
                      onChange={(e) => setProfileForm({ ...profileForm, title: e.target.value })}
                      data-testid="title-select"
                    >
                      <option value="">Select</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Dr">Dr</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact">Contact Number</Label>
                  <Input
                    id="contact"
                    value={profileForm.contact_number}
                    onChange={(e) => setProfileForm({ ...profileForm, contact_number: e.target.value })}
                    data-testid="contact-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="workstation">Work Station</Label>
                    <Input
                      id="workstation"
                      value={profileForm.work_station}
                      onChange={(e) => setProfileForm({ ...profileForm, work_station: e.target.value })}
                      data-testid="workstation-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="specialty">Specialty</Label>
                    <Input
                      id="specialty"
                      value={profileForm.specialty}
                      onChange={(e) => setProfileForm({ ...profileForm, specialty: e.target.value })}
                      data-testid="specialty-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Residential Address</Label>
                  <Input
                    id="address"
                    value={profileForm.residential_address}
                    onChange={(e) => setProfileForm({ ...profileForm, residential_address: e.target.value })}
                    data-testid="address-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" disabled={saving} data-testid="save-profile-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate('/dashboard/profile')}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Next of Kin Tab */}
        <TabsContent value="kin">
          <Card>
            <CardHeader>
              <CardTitle>Edit Next of Kin</CardTitle>
              <CardDescription>Update your emergency contact information</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleKinUpdate} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="kin-first-name">First Name</Label>
                    <Input
                      id="kin-first-name"
                      value={kinForm.first_name || ''}
                      onChange={(e) => setKinForm({ ...kinForm, first_name: e.target.value })}
                      data-testid="kin-first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="kin-surname">Surname</Label>
                    <Input
                      id="kin-surname"
                      value={kinForm.surname || ''}
                      onChange={(e) => setKinForm({ ...kinForm, surname: e.target.value })}
                      data-testid="kin-surname-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="kin-relationship">Relationship</Label>
                    <Input
                      id="kin-relationship"
                      value={kinForm.relationship || ''}
                      onChange={(e) => setKinForm({ ...kinForm, relationship: e.target.value })}
                      data-testid="kin-relationship-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="kin-contact">Contact Number</Label>
                    <Input
                      id="kin-contact"
                      value={kinForm.contact_number || ''}
                      onChange={(e) => setKinForm({ ...kinForm, contact_number: e.target.value })}
                      data-testid="kin-contact-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="kin-address">Residential Address</Label>
                  <Input
                    id="kin-address"
                    value={kinForm.residential_address || ''}
                    onChange={(e) => setKinForm({ ...kinForm, residential_address: e.target.value })}
                    data-testid="kin-address-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" disabled={saving} data-testid="save-kin-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate('/dashboard/profile')}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Password Tab */}
        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                <div>
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                      required
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                    >
                      {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      required
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                    >
                      {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Must be at least 8 characters
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                      required
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                    >
                      {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                    <p className="text-xs text-destructive mt-1">Passwords do not match</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="submit" 
                    disabled={saving || passwordForm.new_password !== passwordForm.confirm_password}
                    data-testid="change-password-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    {saving ? 'Changing...' : 'Change Password'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate('/dashboard/profile')}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </MemberLayout>
  );
};

export default MemberProfileEdit;
